<template>
  <div class="page-container cash-register-page cash-register-session-detail">
    <portal to="page-name">Tesouraria</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <button class="btn btn-gray btn-icon-left float-right ml-2" v-if="!loading && session"
                :class="{loading: printing}" :disabled="printing" @click="printReport">
          <fa-icon :icon="['fal', 'print']"/>Imprimir
        </button>
        <template v-if="!loading && session && session.status === 'closed'">
          <button class="btn btn-primary btn-icon-left float-right ml-2"
                  v-if="!isReviewed(session.review)"
                  @click="review.show = true">
            <fa-icon :icon="['fal', 'check']"/>Conferir
          </button>
          <button class="btn btn-gray btn-icon-left float-right"
                  v-if="canReopen"
                  @click="reopen.show = true">
            <fa-icon :icon="['fal', 'unlock']"/>Reabrir
          </button>
        </template>
        <h1 class="card-title">
          <router-link to="/financial/cash-register-sessions" class="text-gray mr-1">
            <fa-icon :icon="['fal', 'arrow-left']"/>
          </router-link>
          Caixa de {{ session ? session.user.name : '' }}
        </h1>
      </div>
      <div class="card-body" v-if="loading">
        <div class="loading loading-lg"></div>
      </div>
      <div class="card-body" v-else-if="session">
        <div class="cr-summary">
          <div class="cr-summary-item text-left">
            <small>Abertura</small>
            <div class="cr-summary-value">{{ session.opening.date | date('datetime') }}</div>
          </div>
          <div class="cr-summary-item">
            <small>Fundo · origem</small>
            <div class="cr-summary-value">
              {{ session.opening.fund | currency }}
              <small class="text-gray">{{ fundSource }}</small>
            </div>
          </div>
          <div class="cr-summary-item">
            <small>Recebido</small>
            <div class="cr-summary-value">{{ netReceipts | currency }}</div>
          </div>
          <div class="cr-summary-item">
            <small>Suprimentos / Sangrias / Despesas</small>
            <div class="cr-summary-value">
              {{ session.totals.supplies | currency }} /
              {{ session.totals.withdrawals | currency }} /
              {{ session.totals.expenses | currency }}
            </div>
          </div>
          <div class="cr-summary-item" :class="session.status === 'closed' ? '' : 'highlight'">
            <small>{{ session.status === 'closed' ? 'Fechamento' : 'Dinheiro em caixa' }}</small>
            <div class="cr-summary-value">
              <template v-if="session.status === 'closed'">
                {{ session.closing.date | date('datetime') }}
              </template>
              <template v-else>{{ session.totals.cashBalance | currency }}</template>
            </div>
          </div>
        </div>

        <template v-if="session.status === 'closed'">
          <div class="cr-box">
            <div class="cr-box-title">
              Fechamento
              <span class="label ml-1"
                    :class="reviewClass">{{ reviewLabel(session.review) }}</span>
            </div>
            <table class="table">
              <thead>
              <tr>
                <th>Forma</th>
                <th class="text-right">Esperado</th>
                <th class="text-right">Informado</th>
                <th class="text-right">Diferença</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(line, i) in session.closing.expected" :key="i">
                <td>{{ line.method.name }}</td>
                <td class="text-right">{{ line.value | currency }}</td>
                <td class="text-right">{{ session.closing.counted[i].value | currency }}</td>
                <td class="text-right text-bold"
                    :class="{'text-error': session.closing.difference[i].value !== 0}">
                  {{ session.closing.difference[i].value | currency }}
                </td>
              </tr>
              </tbody>
            </table>
            <div class="columns">
              <div class="column col-4 col-sm-12">
                <small class="text-gray">Ficou na gaveta</small>
                <div class="text-bold">{{ session.closing.keptAsFund | currency }}</div>
              </div>
              <div class="column col-4 col-sm-12" v-if="session.closing.withdrawal">
                <small class="text-gray">
                  Sangria para {{ session.closing.withdrawal.bankAccount.name }}
                </small>
                <div class="text-bold">{{ session.closing.withdrawal.value | currency }}</div>
              </div>
              <div class="column col-4 col-sm-12" v-if="session.closing.notes">
                <small class="text-gray">Observações</small>
                <div>{{ session.closing.notes }}</div>
              </div>
              <div class="column col-12 mt-2" v-if="session.review.user">
                <small class="text-gray">
                  Conferido por {{ session.review.user.name }}
                  em {{ session.review.date | date('datetime') }}
                  <template v-if="session.review.notes"> · {{ session.review.notes }}</template>
                </small>
              </div>
            </div>
          </div>
          <div class="cr-box" v-if="session.reopenings.length > 0">
            <div class="cr-box-title">Reaberturas</div>
            <div v-for="(item, i) in session.reopenings" :key="i">
              {{ item.date | date('datetime') }} · {{ item.user.name }} · {{ item.reason }}
            </div>
          </div>
        </template>

        <div class="cr-panel cr-panel-entries">
          <div class="cr-panel-head">
            <div class="cr-panel-title">
              <fa-icon :icon="['fal', 'list']" class="cr-panel-icon"/>
              Movimentos
              <span class="label label-rounded ml-1" v-if="entries.length > 0">
                {{ entries.length }}
              </span>
            </div>
          </div>
          <entries-table :items="entries"
                         :loading="loadingEntries"
                         :allow-reverse="false"
                         @refresh="loadEntries"/>
        </div>
      </div>
    </div>
    <forbidden v-else></forbidden>

    <review-modal v-if="review.show" :show="review.show" :session="session" @close="closeReview"/>
    <reason-modal
      v-if="reopen.show"
      :show="reopen.show"
      title="Reabrir caixa"
      label="Motivo da reabertura"
      confirm-label="Reabrir"
      :message="reopenMessage"
      :saving="reopen.saving"
      @confirm="doReopen"
      @close="reopen.show = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { LIST_CASHIERS, REOPEN_CASHIER } from '@/data/actions/modules/financial';
import { getReviewLabel, getReviewClass, isReviewed } from '@/data/cash-register-entry-types';
import Forbidden from '@/components/auth/Forbidden.vue';
import EntriesTable from './EntriesTable.vue';
import ReviewModal from '../modals/Review.vue';
import ReasonModal from '../modals/Reason.vue';

export default {
  components: {
    Forbidden,
    EntriesTable,
    ReviewModal,
    ReasonModal,
  },
  data() {
    return {
      loading: true,
      loadingEntries: false,
      printing: false,
      session: null,
      entries: [],
      review: { show: false },
      reopen: { show: false, saving: false },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    hasAccess() {
      return this.$can(LIST_CASHIERS);
    },
    netReceipts() {
      const { totals } = this.session;
      return typeof totals.netReceipts === 'number' ? totals.netReceipts : totals.receipts || 0;
    },
    canReopen() {
      const settings = this.user.branch.settings.cashRegister || {};
      return settings.allowReopen && this.$can(REOPEN_CASHIER);
    },
    fundSource() {
      const { source } = this.session.opening;
      if (!source) return '';
      const { currency } = this.$options.filters;
      if (source.type === 'bank_account') {
        const account = source.bankAccount ? source.bankAccount.name : 'conta';
        if (source.kept > 0) {
          return `${currency(source.kept)} da gaveta + ${currency(source.value)} de ${account}`;
        }
        return `de ${account}`;
      }
      return 'ficou na gaveta';
    },
    reopenMessage() {
      return 'O caixa volta a ficar aberto para o operador. '
        + 'O fechamento anterior é descartado e a reabertura fica registrada.';
    },
    reviewClass() {
      return getReviewClass(this.session.review);
    },
  },
  mounted() {
    if (this.hasAccess) {
      this.load();
    }
  },
  methods: {
    reviewLabel: getReviewLabel,
    isReviewed,
    async load() {
      this.loading = true;
      try {
        const { data } = await this.$http.get(`/cash-register/sessions/${this.$route.params.id}`);
        this.session = data;
        await this.loadEntries();
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    loadEntries() {
      this.loadingEntries = true;
      const params = { sessionId: this.session.id, limit: 0 };
      return this.$http.get('/cash-register/entries', { params })
        .then(({ data }) => {
          this.entries = data.items;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingEntries = false;
        });
    },
    printReport() {
      this.printing = true;
      this.$file.print(`/cash-register/sessions/${this.session.id}/report`)
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
    closeReview(saved) {
      this.review.show = false;
      if (saved) this.load();
    },
    doReopen(reason) {
      this.reopen.saving = true;
      this.$http.post(`/cash-register/sessions/${this.session.id}/reopen`, { reason })
        .then(() => {
          this.$toast.success('Caixa reaberto.');
          this.reopen.show = false;
          this.load();
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.reopen.saving = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "../cash-register.scss";
</style>
